import { ButtonHTMLAttributes, FC, Fragment, useCallback, useState } from 'react'

import { useLanguage } from '@/hooks/Contexts'
import { Func1 } from '@/utils/Types'

export const useLoadMore = (initialList: Array<Record<string, any>> = [], initialTotal = 0) => {
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [total, setTotal] = useState<number>(initialTotal)
  // Seeded from SSR props where the caller has them: a list that only fills in on the client
  // renders as "no data" for crawlers, however public the underlying API is.
  const [list, setList] = useState<Array<Record<string, any>>>(initialList)
  const resetParams = useCallback(() => {
    setPageIndex(0)
    setTotal(0)
    setList([])
  }, [])
  const loadMore = () => {
    if (list?.length < total) {
      setPageIndex(prev => prev + 1)
    }
  }
  // Page 1 replaces the list instead of appending to it: `list` is seeded from SSR with page 1
  // already in it, so appending the client's own page-1 fetch shows every row twice. Reset paths
  // (month filter, reload) also land on page 1, with the list already emptied, so replacing is
  // right there too. `dedupeBy` is for lists whose pages can legitimately overlap.
  const appendPage = useCallback((items: Array<Record<string, any>> = [], dedupeBy?: Func1<Record<string, any>, unknown>) => {
    setList(prev =>
      items.reduce((arr, item) => (dedupeBy && arr.some(row => dedupeBy(row) == dedupeBy(item)) ? arr : arr.concat(item)), pageIndex === 0 ? [] : prev)
    )
  }, [pageIndex])
  return { pageIndex, setPageIndex, total, setTotal, list, setList, resetParams, loadMore, appendPage }
}

const LoadMoreButton: FC<ButtonHTMLAttributes<HTMLButtonElement> & { current?: number; total?: number }> = ({ current = 0, total = 0, className, ...rest }) => {
  const {
    actions: { load_more },
  } = useLanguage()
  return (
    <button {...rest} className={`btn-rd-amber btn-hover-amber btn-width ${className || ''}`} disabled={current >= total}>
      {load_more}
      {total > 0 ? (
        <Fragment>
          &nbsp;{current}/{total}
        </Fragment>
      ) : null}
    </button>
  )
}
export default LoadMoreButton
